import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getUser } from '@utils/getUser';
import { Observable } from 'rxjs';
import { API_ROOT } from '../device-id.service';

/**
 * Manda el contexto de agencia y organización del usuario en cada request al
 * gateway. El back los espera del front: `UserContextFilter` (notification_lib)
 * los lee de los headers y los deja en el `RequestContext` que después consulta
 * `AuthService`. Mismo enfoque que en `carnival-lleego-app`, que ya los manda.
 *
 * **`x-delegatorAgencyId`** — la agencia sobre la que se opera. Va en camelCase
 * porque así está declarado en `CustomHeaders.java` del back
 * (`X_DELEGATOR_AGENCY_ID = "x-delegatorAgencyId"`); NO es
 * `x-delegator-agency-id`. Tourbohome no tiene cambio de agencia, así que
 * siempre es la del usuario logueado.
 *
 * No es opcional: `ServiceUtils.resolveAgencyContext` evalúa
 * `!Objects.equals(agencyIdLogged, delegatorAgencyId)`, y con el header ausente
 * esa comparación da `true` para TODO usuario `ORGANIZATION` o `CONSOLIDATOR`.
 * Entra entonces a la rama de delegación con un id nulo y el endpoint responde
 * `400 Missing path variable value 'agencyId'`.
 *
 * **`x-organization-id`** — sin él, el scope `ORGANIZATION` de `/issuances` cae
 * a un camino heredado que sólo recorre el subárbol directo del referente y se
 * PIERDE los consolidadores hermanos. No falla: devuelve de menos, en silencio.
 * Por eso viaja junto al anterior y no cuando alguien lo note.
 *
 * Van en un solo interceptor, y no en uno por header, porque salen del mismo
 * lugar y se resuelven en un único `clone()`.
 */
@Injectable()
export class UserContextInterceptor implements HttpInterceptor {
  static readonly DELEGATOR_AGENCY_HEADER = 'x-delegatorAgencyId';
  static readonly ORGANIZATION_HEADER = 'x-organization-id';

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(API_ROOT)) {
      return next.handle(request);
    }

    const headers = this.contextHeaders();
    if (!Object.keys(headers).length) {
      return next.handle(request);
    }

    return next.handle(request.clone({ setHeaders: headers }));
  }

  private contextHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // `getUser()` hace `JSON.parse` sin red de contención: devuelve `null` si no
    // hay sesión (login, refresh) y TIRA si la clave quedó corrupta. Como este
    // interceptor corre en toda request, dejar propagar esa excepción rompería
    // hasta el login, que es justamente por donde se sale de ese estado.
    let user: ReturnType<typeof getUser> | null = null;
    try {
      user = getUser();
    } catch {
      return headers;
    }

    if (user?.agency) {
      headers[UserContextInterceptor.DELEGATOR_AGENCY_HEADER] = user.agency;
    }

    if (user?.organizationId) {
      headers[UserContextInterceptor.ORGANIZATION_HEADER] = user.organizationId;
    }

    return headers;
  }
}
